<template>
  <transition :name="animation">
    <div class="derslig-toast" :class="[type, positionClass, responsive ? 'responsive' : '']" v-if="visible">
      <div class="derslig-toast-progress" v-if="showProgress" :style="progressStyle"></div>
      <div class="derslig-toast-icon" v-if="showIcon">
        <div class="icon-container">
          <InfoIcon v-if="type === 'info'" />
          <SuccessIcon v-else-if="type === 'success'" />
          <ErrorIcon v-else-if="type === 'error'" />
          <WarningIcon v-else-if="type === 'warning'" />
          <DangerIcon v-else-if="type === 'danger'" />
          <HappyIcon v-else-if="type === 'happy'" />
          <SadIcon v-else-if="type === 'sad'" />
        </div>
      </div>
      <div class="derslig-toast-content">
        <div class="derslig-toast-heading" v-if="heading || autoHeading">{{ heading || typeName }}</div>
        <div class="derslig-toast-text" v-html="text"></div>
      </div>
      <div class="derslig-toast-close" v-if="allowClose" @click="close">×</div>
    </div>
  </transition>
</template>

<script>
import InfoIcon from './icon/InfoIcon.vue';
import SuccessIcon from './icon/SuccessIcon.vue';
import ErrorIcon from './icon/ErrorIcon.vue';
import WarningIcon from './icon/WarningIcon.vue';
import DangerIcon from './icon/DangerIcon.vue';
import HappyIcon from './icon/HappyIcon.vue';
import SadIcon from './icon/SadIcon.vue';

export default {
  name: "DersligToast",
  components: {
    InfoIcon,
    SuccessIcon,
    ErrorIcon,
    WarningIcon,
    DangerIcon,
    HappyIcon,
    SadIcon
  },
  props: {
    heading: {
      type: String,
      default: null
    },
    text: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: 'info',
      validator: function(value) {
        return ['info', 'success', 'error', 'warning', 'danger', 'happy', 'sad'].indexOf(value) !== -1;
      }
    },
    duration: {
      type: Number,
      default: 5000
    },
    position: {
      type: String,
      default: 'top-center',
      validator: function(value) {
        return [
          'top-left', 'top-center', 'top-right',
          'bottom-left', 'bottom-center', 'bottom-right',
          'mid-center'
        ].indexOf(value) !== -1;
      }
    },
    allowClose: {
      type: Boolean,
      default: true
    },
    showIcon: {
      type: Boolean,
      default: true
    },
    playSound: {
      type: Boolean,
      default: false
    },
    soundSrc: {
      type: String,
      default: "/assets/sounds/toast_alert.mp3"
    },
    autoHeading: {
      type: Boolean,
      default: true
    },
    animation: {
      type: String,
      default: 'slide',
      validator: function(value) {
        return ['slide', 'fade', 'bounce', 'none'].indexOf(value) !== -1;
      }
    },
    responsive: {
      type: Boolean,
      default: true
    },
    showProgress: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    typeName() {
      switch(this.type) {
        case 'info': return 'Bilgi';
        case 'success': return 'Başarılı';
        case 'error': return 'Hata';
        case 'danger': return 'Tehlike';
        case 'warning': return 'Uyarı';
        case 'happy': return 'Mutlu';
        case 'sad': return 'Üzgün';
        default: return '';
      }
    },
    positionClass() {
      return `position-${this.position}`;
    },
    progressStyle() {
      return {
        width: `${this.progressWidth}%`
      };
    }
  },
  data() {
    return {
      visible: true,
      timer: null,
      progressTimer: null,
      progressWidth: 100,
      toastAlertSound: null
    };
  },
  mounted() {
    if (this.playSound) {
      this.toastAlertSound = new Audio(this.soundSrc);
      this.toastAlertSound.play().catch(err => {
        console.error('Ses çalınamadı:', err);
      });
    }

    if (this.duration > 0) {
      this.timer = setTimeout(() => {
        this.close();
      }, this.duration);

      if (this.showProgress) {
        this.startProgressBar();
      }
    }

    // Ekran yönü değiştiğinde responsive davranış
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
    if (this.toastAlertSound) {
      this.toastAlertSound.pause();
      this.toastAlertSound = null;
    }
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    close() {
      try {
        this.visible = false;
        this.$emit('close');
      } catch (error) {
        console.error('Toast kapatılırken hata oluştu:', error);
      }
    },
    startProgressBar() {
      const updateInterval = 10; // 10ms
      const totalSteps = this.duration / updateInterval;
      const stepSize = 100 / totalSteps;
      
      this.progressWidth = 100;
      this.progressTimer = setInterval(() => {
        this.progressWidth -= stepSize;
        if (this.progressWidth <= 0) {
          clearInterval(this.progressTimer);
        }
      }, updateInterval);
    },
    handleResize() {
      if (this.responsive) {
        // Responsive davranış kodları
      }
    }
  }
}
</script>

<style scoped>
.derslig-toast {
  display: flex;
  align-items: center;
  width: auto;
  min-width: 250px;
  max-width: 350px;
  padding: 12px 15px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  background-color: #fff;
  position: relative;
  overflow: hidden;
  margin-bottom: 15px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  z-index: 9999;
}

.derslig-toast.responsive {
  max-width: min(350px, 80vw);
}

@media (max-width: 576px) {
  .derslig-toast.responsive {
    min-width: auto;
    max-width: calc(100vw - 40px);
    margin-left: auto;
    margin-right: auto;
    padding: 10px;
  }
  
  .derslig-toast-heading {
    font-size: 14px !important;
  }
  
  .derslig-toast-text {
    font-size: 13px !important;
  }
}

.derslig-toast::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
}

.derslig-toast-progress {
  position: absolute;
  height: 3px;
  background-color: rgba(255, 255, 255, 0.3);
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  transition: width 0.1s linear;
}

.derslig-toast.info::before {
  background-color: #3498db;
}

.derslig-toast.success::before {
  background-color: #2ecc71;
}

.derslig-toast.error::before {
  background-color: #e74c3c;
}

.derslig-toast.warning::before {
  background-color: #f39c12;
}

.derslig-toast.danger::before {
  background-color: #e74c3c;
}

.derslig-toast.happy::before {
  background-color: #3498db;
}

.derslig-toast.sad::before {
  background-color: #9b59b6;
}

.derslig-toast-icon {
  flex: 0 0 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
}

.icon-container {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.derslig-toast.info .derslig-toast-icon {
  color: #3498db;
}

.derslig-toast.success .derslig-toast-icon {
  color: #2ecc71;
}

.derslig-toast.error .derslig-toast-icon {
  color: #e74c3c;
}

.derslig-toast.warning .derslig-toast-icon {
  color: #f39c12;
}

.derslig-toast.danger .derslig-toast-icon {
  color: #e74c3c;
}

.derslig-toast.happy .derslig-toast-icon {
  color: #3498db;
}

.derslig-toast.sad .derslig-toast-icon {
  color: #9b59b6;
}

.derslig-toast-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.derslig-toast-heading {
  font-weight: 600;
  margin-bottom: 4px;
  font-size: 16px;
  color: #333;
}

.derslig-toast-text {
  word-break: break-word;
  font-size: 14px;
  color: #666;
  line-height: 1.4;
}

.derslig-toast-close {
  cursor: pointer;
  font-size: 20px;
  margin-left: 10px;
  opacity: 0.5;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  width: 24px;
  color: #999;
}

.derslig-toast-close:hover {
  opacity: 1;
}

/* Pozisyon sınıfları */
.position-top-left, .position-top-center, .position-top-right,
.position-bottom-left, .position-bottom-center, .position-bottom-right,
.position-mid-center {
  position: fixed;
}

.position-top-left {
  top: 20px;
  left: 20px;
}

.position-top-center {
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
}

.position-top-right {
  top: 20px;
  right: 20px;
}

.position-bottom-left {
  bottom: 20px;
  left: 20px;
}

.position-bottom-center {
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
}

.position-bottom-right {
  bottom: 20px;
  right: 20px;
}

.position-mid-center {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* Animasyon stilleri */
.slide-enter-active {
  animation: slideIn 0.3s ease;
}

.slide-leave-active {
  animation: slideOut 0.3s ease;
}

@keyframes slideIn {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes slideOut {
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(-20px);
    opacity: 0;
  }
}

/* Fade animasyonu */
.fade-enter-active {
  animation: fadeIn 0.3s ease;
}

.fade-leave-active {
  animation: fadeOut 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

/* Bounce animasyonu */
.bounce-enter-active {
  animation: bounceIn 0.5s;
}

.bounce-leave-active {
  animation: bounceOut 0.5s;
}

@keyframes bounceIn {
  0% {
    transform: scale(0.5);
    opacity: 0;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes bounceOut {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(0.5);
    opacity: 0;
  }
}
</style> 